import { Monitor, Moon, Sun } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Preference = "light" | "dark" | "system";
const KEY = "hostdock.theme";

interface ThemeCtx {
  preference: Preference;
  resolved: "light" | "dark";
  setPreference(p: Preference): void;
}
const Ctx = createContext<ThemeCtx | null>(null);

function readPreference(): Preference {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}
const systemDark = () => typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<Preference>(readPreference);
  const [sysDark, setSysDark] = useState(systemDark);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const on = () => setSysDark(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const resolved: "light" | "dark" = preference === "system" ? (sysDark ? "dark" : "light") : preference;
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  const setPreference = useCallback((p: Preference) => {
    setPref(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {
      // storage unavailable (private mode); preference lasts for this page only
    }
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}

const ORDER: Preference[] = ["system", "light", "dark"];
const ICONS = { system: Monitor, light: Sun, dark: Moon } as const;

export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const Icon = ICONS[preference];
  const next = ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length]!;
  return (
    <button
      type="button"
      onClick={() => setPreference(next)}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-fg"
      title={`Theme: ${preference} (switch to ${next})`}
      aria-label={`Theme: ${preference}. Switch to ${next}.`}
    >
      <Icon size={16} />
    </button>
  );
}
